import { useRef, Fragment } from 'react'
import { usePathname } from 'next/navigation'
import Link from '@/components/ui/Link'
import { PREFETCH } from '@/lib/prefetch'
import Pill from '@/components/ui/Pill'
import AppIcon from '@/components/content/app/AppIcon'
import { getBrandColors, cn, getOverlappingAppCategory } from '@/lib/utils'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import { createLogger } from '@/lib/logger'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { App, ComparisonAppsApp, Comparison, Media } from '@/payload-types'

const logger = createLogger('ComparisonThumbnail')

type ComparisonThumbnailProps = {
  comparison: Pick<Comparison, 'id' | 'name' | 'comparisonApps' | 'tldr'>
  type?: string
  result?: boolean
}

export default function ComparisonThumbnail({
  comparison,
  type,
  result,
}: ComparisonThumbnailProps) {
  const pathname = usePathname()
  const invert = pathname.startsWith('/best')
  const { md } = useBreakpoint()

  const comparisonApps = (comparison.comparisonApps?.docs as ComparisonAppsApp[])?.filter(
    (ca) => ca.app && typeof ca.app !== 'number' && (ca.app as App).slug,
  )
  const apps: App[] = comparisonApps?.map((comparisonApp) => comparisonApp.app as App) ?? []

  const totalApps = comparisonApps?.length ?? 0

  const appsRef = useRef(apps)
  appsRef.current = apps

  const link = `/compare/${(comparisonApps?.sort((a, b) => a.order - b.order) ?? [])
    .map((obj) => (obj.app as App).slug)
    .join('-vs-')}`

  const overlappingCategory = getOverlappingAppCategory(apps)

  switch (type) {
    case FeaturedTagType.ThumbnailPrimary:
      return (
        <div key={comparison.id} className="flex w-full flex-col gap-3">
          <Link
            href={link}
            prefetch={PREFETCH}
            className={cn(
              'relative flex h-[200px] w-full shrink-0 overflow-hidden rounded-icon-sm border border-black/10 shadow-inner',
            )}
          >
            {apps.map((app, index) => {
              const appIcon = app.icon as Media
              const appGlyph = app.glyph as Media
              if (typeof appIcon === 'number' || typeof appGlyph === 'number') {
                logger.error(
                  `appIcon is not a Media object in ComparisonThumbnail (ComparisonRow) app icon`,
                )
                return null
              }
              const { backgroundColor } = getBrandColors(app)
              return (
                <div
                  key={`${app.id}-${index}`}
                  className="absolute top-0 h-full w-full"
                  style={{
                    backgroundColor,
                    clipPath:
                      index === 0
                        ? 'polygon(0 0, 60% 0, 40% 100%, 0% 100%)'
                        : 'polygon(60% 0, 100% 0, 100% 100%, 40% 100%)',
                  }}
                >
                  <div
                    className={cn(
                      'absolute top-1/2 flex -translate-y-1/2 items-center justify-center',
                      index === 0 ? 'left-1/4 -translate-x-1/2' : 'right-1/4 translate-x-1/2',
                    )}
                  >
                    <AppIcon
                      icon={appIcon}
                      glyph={appGlyph}
                      name={app.name}
                      background={backgroundColor}
                      invert={backgroundColor === '#000000'}
                      size={64}
                    />
                  </div>
                </div>
              )
            })}
          </Link>

          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col">
              <Link
                href={link}
                prefetch={PREFETCH}
                className={cn(
                  'line-clamp-1 font-semibold',
                  invert ? 'text-white' : 'text-gray-950 dark:text-white',
                )}
              >
                {comparison.name}
              </Link>
              {overlappingCategory && (
                <span
                  className={cn(
                    'line-clamp-1 w-full text-13',
                    invert ? 'text-white/60' : 'text-gray-600 dark:text-gray-400',
                  )}
                >
                  {overlappingCategory.name}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Link href={link} prefetch={PREFETCH}>
                <Pill style="outlinePrimary" theme={invert ? 'dark' : 'light'} className="ml-auto">
                  Read
                </Pill>
              </Link>
            </div>
          </div>
        </div>
      )
    default:
      return (
        <div
          key={comparison.id}
          className={cn(
            'flex items-center justify-between',
            result ? 'rounded-xl border border-gray-200 p-3' : 'px-2 last:pb-0 md:pb-4',
          )}
        >
          <Link
            href={link}
            prefetch={PREFETCH}
            className="flex w-[calc(100%-90px)] items-center gap-3.5"
          >
            <div className="relative flex shrink-0 items-center gap-2">
              {apps.map((app, index) => {
                const appIcon = app.icon as Media
                const appGlyph = app.glyph as Media
                if (typeof appIcon === 'number' || typeof appGlyph === 'number') {
                  logger.error('appIcon is not a Media object in ComparisonThumbnail app icon')
                  return null
                }

                const { backgroundColor } = getBrandColors(app)
                return (
                  <Fragment key={`${app.id}-${index}`}>
                    <AppIcon
                      icon={appIcon}
                      glyph={appGlyph}
                      name={app.name}
                      background={backgroundColor}
                      size={result || !md ? 40 : 64}
                    />
                    {index < totalApps - 1 && (
                      <span className="text-10 font-semibold uppercase text-gray-400">vs</span>
                    )}
                  </Fragment>
                )
              })}
            </div>

            <div className="flex flex-col">
              <span
                className={cn(
                  'line-clamp-1 font-semibold text-gray-950 dark:text-white',
                  result ? '-mt-1 text-14' : 'text-14 md:text-16',
                )}
              >
                {comparison.name}
              </span>

              {overlappingCategory && (
                <span
                  className={cn(
                    'line-clamp-1 w-full',
                    result ? 'text-12 text-gray-500' : 'text-13 text-gray-600 dark:text-gray-400',
                  )}
                >
                  {overlappingCategory.name}
                </span>
              )}
            </div>
          </Link>

          <div className="flex gap-3">
            <Link href={link} prefetch={PREFETCH}>
              <Pill style="outlinePrimary" className="ml-auto">
                Read
              </Pill>
            </Link>
          </div>
        </div>
      )
  }
}
